/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 20 2023
 **/

import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { usePersistStore } from "../persistence";

function sortedUserList(users: { id: string; name: string; blocked?: boolean }[]) {
	return users.toSorted((a, b) => {
		const languagePriority = (name: string) => {
			if (/^[A-Za-z]/.test(name)) return 1;
			if (/^[ぁ-ゔァ-ヴー々〆〤一-龠]/.test(name)) return 2;
			if (/^[가-힣]/.test(name)) return 3;
			return 4;
		};

		const priorityA = languagePriority(a.name);
		const priorityB = languagePriority(b.name);

		const locale = priorityA === 1 ? "en" : priorityA === 3 ? "ko" : "ja";

		if (priorityA !== priorityB) return priorityA - priorityB;

		return a.name.localeCompare(b.name, locale, { sensitivity: "base" });
	});
}

export function getChatTitleByUserList(
	users: { id: string; name: string; blocked?: boolean }[],
	maxLength = 6
): string {
	return users.length === 1 && users.some((user) => user.id === Configure.userID)
		? users[0].name
		: `${sortedUserList(users.slice(0, maxLength).filter((user) => user.id !== Configure.userID))
				.map((user) => {
					if (user.blocked) {
						return user.name + _T(" (차단됨)");
					} else return user.name;
				})
				.join(", ")}`;
}

function to_json_format(data: ChatServices.Format2.Message | string) {
	try {
		if (typeof data === "string") {
			return JSON.parse(data) as ChatServices.Format2.Message;
		}
		return data;
	} catch {
		return data;
	}
}

function extract_string(item: ChatServices.Format2.Elements.AllRichtextElements): string | null {
	switch (item.type) {
		case "text": {
			return item.text;
		}

		case "section": {
			return item.elements
				.map((item) => extract_string(item))
				.filter(Boolean)
				.join(""); // 수평으로 나열
		}
	}
	return null;
}

function strip_message_format(message: ChatServices.Format2.Message) {
	return message.elements
		.map((item) => extract_string(item))
		.filter(Boolean)
		.join("\n"); // 수직으로 나열
}

export function stripMessageFormat(message: string): string {
	const _message = to_json_format(message);
	if (typeof _message === "string") {
		return _message;
	}
	const r = strip_message_format(_message);
	// console.debug("#ChatUtil# r =", r, message);
	return r;
}

function sortByMessageLastSentTime(
	nextDM: WebServices.DirectMessage.List.Data[number],
	prevDM: WebServices.DirectMessage.List.Data[number]
) {
	if (
		(nextDM.lastSentMessage?.sentTime || nextDM.createdTime) >
		(prevDM.lastSentMessage?.sentTime || prevDM.createdTime)
	) {
		return -1;
	}
	if (
		(prevDM.lastSentMessage?.sentTime || nextDM.createdTime) >
		(nextDM.lastSentMessage?.sentTime || prevDM.createdTime)
	) {
		return 1;
	}
	return 0;
}

export function sortDMList(
	nextDM: WebServices.DirectMessage.List.Data[number],
	prevDM: WebServices.DirectMessage.List.Data[number]
) {
	if (nextDM.pinned && prevDM.pinned) {
		// 둘 다 핀고정 되었을 때
		if ((nextDM.pinUpdatedTime || 0) > (prevDM.pinUpdatedTime || 0)) {
			return -1;
		}
		if ((prevDM.pinUpdatedTime || 0) > (nextDM.pinUpdatedTime || 0)) {
			return 1;
		}
	} else {
		// 둘 중 하나라도 핀고정 되어있지 않을 때
		if (nextDM.pinned) {
			return -1;
		}
		if (prevDM.pinned) {
			return 1;
		}
	}

	if (nextDM.hasNewMessage && prevDM.hasNewMessage) {
		return sortByMessageLastSentTime(nextDM, prevDM);
	} else {
		// 둘 중 하나라도 새로운 메시지가 없을 때
		if (nextDM.hasNewMessage) {
			return -1;
		}
		if (prevDM.hasNewMessage) {
			return 1;
		}
		return sortByMessageLastSentTime(nextDM, prevDM);
	}
}

export function hasNewMessageInSpaceChat<S, R>(
	space: S & { rooms: (R & { hide: boolean; deleted: boolean; hasNewMessage: boolean })[] }
) {
	return space.rooms.filter((room) => !(room.deleted || room.hide)).some((room) => room.hasNewMessage);
}

export function hasPinMessageInSpaceChat<S, R>(
	space: S & { rooms: (R & { hide: boolean; deleted: boolean; pinned: boolean })[] }
) {
	return space.rooms.filter((room) => !(room.deleted || room.hide)).some((room) => room.pinned);
}

export function hasDraftMessageInSpaceChat<S, R>(
	space: S & { rooms: (R & { hide: boolean; deleted: boolean; roomId: string })[] }
): boolean {
	const hasDraft = usePersistStore.getState().hasDraft;

	const visibleRooms = space.rooms.filter((room) => !(room.deleted || room.hide));

	return Object.keys(hasDraft).some(
		(roomId) => hasDraft[roomId] === true && visibleRooms.some((room) => room.roomId === roomId)
	);
}

export function isMessageFromMe(data: ChatServices.Server.MessageSent.Data) {
	const chatForm = JSON.parse(data.message) as ChatServices.Format2.Container.System | undefined;
	/**
	 * system 메시지 타입의 상위 depth의 sender 필드는 "보내는사람"이 아니다.
	 */
	if (chatForm?.type === "system") {
		const item = chatForm.elements.at(0);
		if (item) {
			switch (item.key) {
				case "hangupNoResponseCall": {
					return item.data.caller.id === Configure.userID;
				}
				case "voiceChatEnd":
				case "voiceChatStart": {
					// 멤버ID가 본인일 경우 red dot 노출 x
					return item.data.memberId === Configure.userID;
				}
				case "voiceChatJoin": {
					return true;
				}
				default: {
					return false;
				}
			}
		}
	} else {
		return data.sender.id === Configure.userID;
	}
	return false;
}
